import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserHelperService } from '@helpers/user-helper.service';
import { CallHistoryI, CreateCallHistoryI } from '@interfaces/call-history.interface';
import { PageI } from '@interfaces/page.interface';
import { Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { UserSocketLoginI } from '@interfaces/user.interface';

export interface PendingTabulationI {
  callHistoryId: string;
  contactName?: string;
  number?: string;
  protocolNumber?: string;
  duration?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CallHistoryService {
  private baseUrl = environment.OMNISMART_API_CALL_HISTORY;
  private slug_url = 'call-history';

  constructor(private http: HttpClient, private userHelperService: UserHelperService) {}

  public insertCall(callHistory: CreateCallHistoryI): Promise<CallHistoryI> {
    return firstValueFrom(this.http.post<CallHistoryI>(this.baseUrl + this.slug_url, callHistory));
  }

  public updateCall(id: string, callHistory: Partial<CreateCallHistoryI>): Promise<CallHistoryI> {
    return firstValueFrom(this.http.patch<CallHistoryI>(`${this.baseUrl}${this.slug_url}/${id}`, callHistory));
  }

  public getCall(queryParams?: {
    search?: string;
    page?: number;
    limit?: number;
    active?: string;
    sort?: string;
    hideGroup?: boolean;
  }): Observable<PageI<CallHistoryI>> {
    const userId = this.userHelperService.getUserAttributes()['custom:userId'];

    let params = new HttpParams()
      .set('search', queryParams?.search ?? '')
      .set('sort', queryParams?.sort ?? 'asc')
      .set('active', queryParams?.active ?? 'createdAt')
      .set('limit', (queryParams?.limit ?? 10).toString())
      .set('page', (queryParams?.page ?? 1).toString())
      .set('status', status);

    return this.http.get<PageI<CallHistoryI>>(this.baseUrl + this.slug_url, {
      params,
    });
  }

  public createTabulation(options: { callHistoryId: string; tabulationIds: string[]; comment?: string }) {
    const { callHistoryId, tabulationIds, comment } = options;
    const url = `${this.baseUrl}${this.slug_url}/${callHistoryId}/tabulation`;

    return this.http.post<CallHistoryI>(url, { tabulationIds, comment });
  }

  public lostConnection(callHistoryId: string) {
    const url = `${this.baseUrl}${this.slug_url}/${callHistoryId}/lost-connection`;

    return this.http.patch(url, {});
  }

  public setPendingTabulation(payload: PendingTabulationI): Observable<unknown> {
    const url = `${this.baseUrl}${this.slug_url}/pending-tabulation`;

    return this.http.post(url, payload);
  }

  public getPendingTabulation(): Observable<PendingTabulationI | null> {
    const url = `${this.baseUrl}${this.slug_url}/pending-tabulation`;

    return this.http.get<PendingTabulationI | null>(url);
  }

  public async createSipLog(type: string, data: any, user: UserSocketLoginI) {
    const url = `${this.baseUrl}${this.slug_url}/sip-log`;
    try {
      await firstValueFrom(
        this.http.post(url, {
          type,
          data: {
            ...data,
            userId: user?._id,
            customerId: user?.customerId,
            ramal: user?.ramal,
            timestamp: new Date().getTime(),
          },
        })
      );
    } catch (error) {
      console.error(error);
    }
  }
}
